import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatCLP } from "../../lib/format";
import { getRoute, formatDuration } from "../../lib/geo";
import { LocationPicker } from "../../components/LocationPicker";
import { RouteMap } from "../../components/RouteMap";
import { MapPin, MapPinLine, Package, CalendarBlank, CurrencyDollar, CheckCircle, ArrowLeft, ArrowRight, CircleNotch, Lightning, Truck, Ruler, Clock } from "@phosphor-icons/react";

const STEP_TITLES = ["¿Desde dónde?", "¿Hasta dónde?", "¿Qué transportas?", "¿Cuándo?", "Tu precio", "Confirmación"];
const emptyLoc = { region: "", comuna: "", address: "", reference: "", lat: null, lng: null };

export default function PublishWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [config, setConfig] = useState({ categories: [], cargoVehicleMap: {} });
  const [loading, setLoading] = useState(false);
  const [route, setRoute] = useState(null);
  const [routing, setRouting] = useState(false);
  const [form, setForm] = useState({
    origin: { ...emptyLoc }, destination: { ...emptyLoc },
    category: "", description: "", approximateQuantity: "", approximateWeight: "", dimensions: "", observations: "",
    requestedDate: "", timeRange: "", asap: false, proposedPrice: "",
  });

  useEffect(() => { api.get("/meta/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);

  // Calcula la ruta cuando ambas ubicaciones tienen coordenadas.
  useEffect(() => {
    const o = form.origin, d = form.destination;
    if (o.lat != null && d.lat != null) {
      setRouting(true);
      getRoute(o, d).then((r) => { setRoute(r); setRouting(false); });
    } else {
      setRoute(null);
    }
  }, [form.origin.lat, form.origin.lng, form.destination.lat, form.destination.lng]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const recommended = config.cargoVehicleMap?.[form.category] || [];

  const canNext = () => {
    if (step === 0) return form.origin.region && form.origin.comuna && form.origin.lat != null;
    if (step === 1) return form.destination.region && form.destination.comuna && form.destination.lat != null;
    if (step === 2) return form.category;
    if (step === 3) return form.asap || (form.requestedDate && form.timeRange);
    if (step === 4) return Number(form.proposedPrice) > 0;
    return true;
  };

  const toLoc = (l) => ({ address: l.address || l.comuna, city: l.comuna, region: l.region, comuna: l.comuna, reference: l.reference, lat: l.lat, lng: l.lng });

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        origin: toLoc(form.origin),
        destination: toLoc(form.destination),
        proposedPrice: Number(form.proposedPrice),
        distanceKm: route?.distanceKm ?? null,
        durationMin: route?.durationMin ?? null,
      };
      const { data } = await api.post("/freights", payload);
      toast.success("¡Flete publicado! Buscando transportistas...");
      navigate(`/app/freight/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al publicar");
      setLoading(false);
    }
  };

  const TIME_RANGES = ["09:00 - 12:00", "12:00 - 14:00", "14:00 - 16:00", "16:00 - 18:00", "18:00 - 20:00"];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="publish-wizard">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <button onClick={() => (step === 0 ? navigate(-1) : setStep(step - 1))} className="flex items-center gap-1 text-sm font-semibold text-zinc-600 hover:text-[#0F1115]" data-testid="wizard-back">
            <ArrowLeft size={16} weight="bold" /> Atrás
          </button>
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-400">Paso {step + 1} de 6</span>
        </div>
        <div className="h-2 rounded-full bg-zinc-200 overflow-hidden">
          <div className="h-full bg-[#FF5A36] transition-all duration-300" style={{ width: `${((step + 1) / 6) * 100}%` }} />
        </div>
      </div>

      <h1 className="font-display text-3xl font-bold tracking-tight mb-6">{STEP_TITLES[step]}</h1>

      <div className="space-y-4">
        {step === 0 && <LocationPicker testid="origin" value={form.origin} onChange={(v) => setForm({ ...form, origin: v })} />}
        {step === 1 && <LocationPicker testid="destination" value={form.destination} onChange={(v) => setForm({ ...form, destination: v })} />}

        {step === 1 && route && (
          <div className="rounded-2xl border border-zinc-200 bg-white p-4 flex items-center justify-around text-center" data-testid="route-summary">
            <div><Ruler size={20} className="text-[#FF5A36] mx-auto" /><div className="font-display text-xl font-black mt-1">{route.distanceKm} km</div><div className="text-xs text-zinc-500">Distancia</div></div>
            <div className="w-px h-10 bg-zinc-200" />
            <div><Clock size={20} className="text-[#FF5A36] mx-auto" /><div className="font-display text-xl font-black mt-1">{formatDuration(route.durationMin)}</div><div className="text-xs text-zinc-500">Tiempo est.</div></div>
          </div>
        )}
        {step === 1 && routing && <div className="text-center text-sm text-zinc-500 flex items-center justify-center gap-2"><CircleNotch size={16} className="animate-spin" /> Calculando ruta...</div>}

        {step === 2 && (
          <>
            <div className="grid grid-cols-2 gap-2.5" data-testid="category-grid">
              {config.categories.map((c) => (
                <button key={c} onClick={() => setForm({ ...form, category: c })} data-testid={`category-${c}`}
                  className={`rounded-xl border p-3.5 text-sm font-medium text-left transition-all ${form.category === c ? "border-[#FF5A36] bg-[#FFF4F1] ring-2 ring-[#FF5A36]" : "border-zinc-200 hover:border-zinc-300"}`}>
                  {c}
                </button>
              ))}
            </div>
            {recommended.length > 0 && (
              <div className="rounded-2xl border border-zinc-200 bg-[#FFF4F1] p-4" data-testid="vehicle-suggestion">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#0F1115]"><Truck size={18} className="text-[#FF5A36]" /> Vehículo sugerido para esta carga</div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {recommended.map((v) => <span key={v} className="text-xs font-semibold bg-white border border-[#FFD9CF] text-[#E04B2A] rounded-full px-3 py-1">{v}</span>)}
                </div>
                <p className="text-xs text-zinc-500 mt-2">Solo transportistas con un vehículo compatible verán tu flete.</p>
              </div>
            )}
            <TextArea label="Descripción" testid="wizard-description" value={form.description} onChange={set("description")} placeholder="Cuéntanos sobre tu carga..." />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Cantidad aprox." testid="wizard-quantity" value={form.approximateQuantity} onChange={set("approximateQuantity")} placeholder="Ej: 10 cajas" />
              <Input label="Peso aprox." testid="wizard-weight" value={form.approximateWeight} onChange={set("approximateWeight")} placeholder="Ej: 300 kg" />
            </div>
            <Input label="Dimensiones (opcional)" testid="wizard-dimensions" value={form.dimensions} onChange={set("dimensions")} placeholder="Ej: 2m x 1m x 1m" />
          </>
        )}

        {step === 3 && (
          <>
            <button onClick={() => setForm({ ...form, asap: !form.asap })} data-testid="wizard-asap"
              className={`w-full rounded-xl border p-4 flex items-center gap-3 transition-all ${form.asap ? "border-[#FF5A36] bg-[#FFF4F1] ring-2 ring-[#FF5A36]" : "border-zinc-200"}`}>
              <Lightning size={22} weight="fill" className="text-[#FF5A36]" />
              <div className="text-left">
                <div className="font-semibold">Lo antes posible</div>
                <div className="text-xs text-zinc-500">Los transportistas responderán cuanto antes</div>
              </div>
            </button>
            {!form.asap && (
              <>
                <Input label="Fecha" testid="wizard-date" type="date" value={form.requestedDate} onChange={set("requestedDate")} />
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Rango horario</label>
                  <div className="grid grid-cols-2 gap-2 mt-1.5" data-testid="time-range-grid">
                    {TIME_RANGES.map((t) => (
                      <button key={t} onClick={() => setForm({ ...form, timeRange: t })} data-testid={`time-${t}`}
                        className={`rounded-xl border p-3 text-sm font-medium transition-all ${form.timeRange === t ? "border-[#FF5A36] bg-[#FFF4F1] ring-2 ring-[#FF5A36]" : "border-zinc-200 hover:border-zinc-300"}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {step === 4 && (
          <div>
            <p className="text-zinc-600 mb-4">¿Cuánto quieres pagar por este flete?</p>
            <div className="relative">
              <span className="absolute left-5 top-1/2 -translate-y-1/2 text-3xl font-black text-zinc-400">$</span>
              <input data-testid="wizard-price" type="number" inputMode="numeric" value={form.proposedPrice} onChange={set("proposedPrice")} autoFocus
                className="w-full h-20 pl-12 pr-24 rounded-2xl border-2 border-zinc-300 bg-white font-display text-4xl font-black focus:outline-none focus:border-[#FF5A36]" placeholder="0" />
              <span className="absolute right-5 top-1/2 -translate-y-1/2 text-lg font-bold text-zinc-400">CLP</span>
            </div>
            {route && <p className="mt-3 text-sm text-zinc-500">Ruta: <b>{route.distanceKm} km</b> · {formatDuration(route.durationMin)}</p>}
            <p className="mt-4 text-sm text-zinc-500 bg-zinc-100 rounded-xl px-4 py-3">Los transportistas podrán aceptar tu precio o enviarte una contraoferta.</p>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4" data-testid="wizard-summary">
            {(form.origin.lat != null && form.destination.lat != null) && (
              <RouteMap origin={form.origin} destination={form.destination} geometry={route?.geometry} />
            )}
            <div className="rounded-2xl border border-zinc-200 bg-white p-6 space-y-4">
              <Row icon={MapPin} label="Origen" value={`${form.origin.address || form.origin.comuna}, ${form.origin.comuna}`} />
              <Row icon={MapPinLine} label="Destino" value={`${form.destination.address || form.destination.comuna}, ${form.destination.comuna}`} />
              {route && <Row icon={Ruler} label="Distancia" value={`${route.distanceKm} km · ${formatDuration(route.durationMin)}`} />}
              <Row icon={Package} label="Carga" value={form.category} />
              {recommended.length > 0 && <Row icon={Truck} label="Vehículo sugerido" value={recommended.join(", ")} />}
              <Row icon={CalendarBlank} label="Cuándo" value={form.asap ? "Lo antes posible" : `${form.requestedDate} · ${form.timeRange}`} />
              <div className="border-t border-zinc-100 pt-4 flex items-center justify-between">
                <span className="flex items-center gap-2 text-zinc-500"><CurrencyDollar size={18} /> Precio ofrecido</span>
                <span className="font-display text-3xl font-black text-[#FF5A36]">{formatCLP(Number(form.proposedPrice))}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-8">
        {step < 5 ? (
          <button onClick={() => setStep(step + 1)} disabled={!canNext()} data-testid="wizard-next"
            className="w-full py-4 rounded-xl bg-[#0F1115] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#1A1D24] transition-colors active:scale-95 disabled:opacity-40">
            Continuar <ArrowRight size={20} weight="bold" />
          </button>
        ) : (
          <button onClick={submit} disabled={loading} data-testid="wizard-submit"
            className="w-full py-4 rounded-xl bg-[#FF5A36] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#E04B2A] transition-colors active:scale-95 disabled:opacity-60">
            {loading ? <CircleNotch size={20} className="animate-spin" /> : <><CheckCircle size={20} weight="bold" /> Publicar flete</>}
          </button>
        )}
      </div>
    </div>
  );
}

function Input({ label, testid, ...props }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">{label}</label>
      <input data-testid={testid} {...props} className="mt-1.5 w-full h-12 px-4 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36] focus:border-transparent" />
    </div>
  );
}
function TextArea({ label, testid, ...props }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">{label}</label>
      <textarea data-testid={testid} rows={3} {...props} className="mt-1.5 w-full px-4 py-3 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36] focus:border-transparent resize-none" />
    </div>
  );
}
function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={18} className="text-zinc-400 mt-0.5" />
      <div>
        <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{label}</div>
        <div className="font-medium">{value}</div>
      </div>
    </div>
  );
}
